/**
 * Collections — what is outstanding, by age, with one-tap chase.
 *
 * An invoice list sorted by date does not tell a workshop who to call. This
 * does: arrears bucketed by how long they have been overdue, oldest first,
 * with WhatsApp / email / copy-link on every row.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Loader2, MessageCircle, Mail, Copy, Check, BellRing, BellOff,
  AlertTriangle, ExternalLink, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { greetName, renderMessage, waUrl } from '../../../lib/waShare';
import { formatErr } from '../../../components/admin/billing/shared';

const inr = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const inr2 = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BUCKET_TONE = {
  current: 'border-slate-200 text-slate-600',
  '1-30': 'border-amber-200 text-amber-700 bg-amber-50/50',
  '31-60': 'border-orange-200 text-orange-700 bg-orange-50/50',
  '61-90': 'border-rose-200 text-rose-700 bg-rose-50/50',
  '90+': 'border-rose-300 text-rose-800 bg-rose-100/60',
};

const CADENCES = [
  { key: 'gentle', days: [7, 21], label: 'Gentle', hint: '7 and 21 days overdue' },
  { key: 'standard', days: [3, 10, 21], label: 'Standard', hint: '3, 10 and 21 days overdue' },
  { key: 'firm', days: [1, 7, 14, 30], label: 'Firm', hint: '1, 7, 14 and 30 days overdue' },
];

const CollectionsPage = () => {
  const { authHeader } = useAuth();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [bucket, setBucket] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [copiedId, setCopiedId] = useState('');

  const load = useCallback(async (b) => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/receivables/summary`, { headers: authHeader() }),
        axios.get(`${API}/receivables/list`, { params: { bucket: b }, headers: authHeader() }),
      ]);
      setSummary(s.data);
      setRows(l.data.rows || []);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { load(bucket); }, [load, bucket]);

  const cadenceKey = useMemo(() => {
    const days = (summary?.reminders?.days || []).join(',');
    return CADENCES.find((c) => c.days.join(',') === days)?.key || 'standard';
  }, [summary]);

  const saveReminders = async (enabled, days) => {
    try {
      const { data } = await axios.put(`${API}/receivables/settings`,
        { enabled, days }, { headers: authHeader() });
      setSummary((s) => ({ ...s, reminders: data }));
      toast.success(enabled
        ? 'Automatic reminders are on. Overdue invoices will be chased for you.'
        : 'Automatic reminders are off.');
    } catch (e) { toast.error(formatErr(e)); }
  };

  const chaseWhatsApp = async (row) => {
    const phone = (row.customer.phone || '').replace(/\D/g, '').slice(-10);
    const message = renderMessage('payment_reminder', {
      customer: greetName(row.customer.prefix, row.customer.name), number: row.number, balance: inr2(row.balance),
      overdue_text: row.days_overdue > 0 ? `${row.days_overdue} days past due` : 'due', pay_link: row.pay_url || '',
    });
    // Open first — a popup blocker will not fire if this is the direct result
    // of the click, and the logging is what happens after.
    window.open(waUrl(phone, message),
      '_blank', 'noopener');
    try {
      await axios.post(`${API}/receivables/${row.kind}/${row.id}/chase`, {},
        { headers: authHeader() });
      await load(bucket);
    } catch { /* the message still went; the log is a nicety */ }
  };

  const chaseEmail = async (row) => {
    setBusyId(row.id);
    try {
      await axios.post(`${API}/receivables/${row.kind}/${row.id}/remind`, {},
        { headers: authHeader() });
      toast.success(`Reminder emailed to ${row.customer.email}`);
      await load(bucket);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusyId(''); }
  };

  const copyLink = async (row) => {
    try {
      await navigator.clipboard.writeText(row.pay_url);
      setCopiedId(row.id);
      setTimeout(() => setCopiedId(''), 1800);
    } catch { toast.error('Could not copy the link'); }
  };

  const remindersOn = Boolean(summary?.reminders?.enabled);

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6" data-testid="collections-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Collections</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Everything still owed to you, oldest first.
          </p>
        </div>
        <button
          onClick={() => load(bucket)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-teal-700 border border-slate-300 rounded-lg px-3 py-2"
          data-testid="collections-refresh"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Headline numbers */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Outstanding</div>
          <div className="text-2xl font-semibold text-slate-900 mt-1" data-testid="total-outstanding">
            ₹ {inr(summary?.total_outstanding)}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Past due</div>
          <div className="text-2xl font-semibold text-rose-700 mt-1" data-testid="overdue-total">
            ₹ {inr(summary?.overdue_total)}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Oldest arrear</div>
          <div className="text-2xl font-semibold text-slate-900 mt-1" data-testid="oldest-days">
            {summary?.oldest_days || 0} <span className="text-base font-normal text-slate-400">days</span>
          </div>
        </div>
      </div>

      {/* Automatic reminders */}
      <div className={`rounded-xl border p-4 mb-5 ${remindersOn ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-200 bg-white'}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex gap-2.5">
            {remindersOn
              ? <BellRing size={17} className="text-emerald-600 mt-0.5 shrink-0" />
              : <BellOff size={17} className="text-slate-400 mt-0.5 shrink-0" />}
            <div>
              <div className="text-sm font-semibold text-slate-900">
                Automatic payment reminders are {remindersOn ? 'on' : 'off'}
              </div>
              <p className="text-xs text-slate-600 mt-0.5 max-w-xl">
                {remindersOn
                  ? 'Overdue invoices are emailed a reminder with your pay link, on the cadence below. Each invoice is chased at most once a day, and never after it is settled.'
                  : 'Turn this on and overdue invoices chase themselves — an email with your pay link, on a cadence you choose. Nothing is sent to a customer until you switch it on.'}
              </p>
              {remindersOn && summary?.reminders?.last_run_at && (
                <p className="text-[11px] text-slate-500 mt-1">
                  Last sweep {new Date(summary.reminders.last_run_at).toLocaleString('en-IN')}
                  {` · ${summary.reminders.last_run_sent} sent`}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => saveReminders(!remindersOn, summary?.reminders?.days || [3, 10, 21])}
            className={`shrink-0 text-sm font-semibold px-4 py-2 rounded-lg ${
              remindersOn
                ? 'border border-slate-300 text-slate-700 hover:border-slate-400'
                : 'bg-teal-500 hover:bg-teal-600 text-white'
            }`}
            data-testid="toggle-reminders-btn"
          >
            {remindersOn ? 'Turn off' : 'Turn on'}
          </button>
        </div>

        {remindersOn && (
          <div className="flex flex-wrap items-center gap-2 mt-3 pl-7">
            {CADENCES.map((c) => (
              <button
                key={c.key}
                onClick={() => saveReminders(true, c.days)}
                className={`text-left rounded-lg border px-3 py-1.5 ${
                  cadenceKey === c.key
                    ? 'border-emerald-500 bg-white ring-1 ring-emerald-500/30'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
                data-testid={`cadence-${c.key}`}
              >
                <div className="text-xs font-semibold text-slate-800">{c.label}</div>
                <div className="text-[10px] text-slate-500">{c.hint}</div>
              </button>
            ))}
          </div>
        )}

        {remindersOn && !summary?.can_pay_online && (
          <div className="flex gap-2 text-xs text-amber-800 mt-3 pl-7" data-testid="no-gateway-warning">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>
              No gateway is connected, so reminders will go out without a Pay now
              link. Connect one in Billing Setup → Payments.
            </span>
          </div>
        )}
      </div>

      {/* Aging buckets */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setBucket('chase')}
          className={`rounded-xl border px-3.5 py-2 text-left ${bucket === 'chase' ? 'border-emerald-500 ring-1 ring-emerald-500/30 bg-white' : 'border-emerald-200 bg-emerald-50/50 hover:border-emerald-300'}`}
          data-testid="bucket-chase"
        >
          <div className="text-[10px] uppercase tracking-wider text-emerald-700">Chase today</div>
          <div className="text-sm font-semibold text-slate-900">₹ {inr(summary?.chase_amount)}</div>
          <div className="text-[10px] text-emerald-700/70">{summary?.chase_count || 0} to call</div>
        </button>
        <button
          onClick={() => setBucket('all')}
          className={`rounded-xl border px-3.5 py-2 text-left ${bucket === 'all' ? 'border-teal-500 ring-1 ring-teal-500/30 bg-white' : 'border-slate-200 bg-white hover:border-slate-300'}`}
          data-testid="bucket-all"
        >
          <div className="text-[10px] uppercase tracking-wider text-slate-400">All</div>
          <div className="text-sm font-semibold text-slate-900">₹ {inr(summary?.total_outstanding)}</div>
        </button>
        {(summary?.buckets || []).map((b) => (
          <button
            key={b.key}
            onClick={() => setBucket(b.key)}
            className={`rounded-xl border px-3.5 py-2 text-left bg-white ${
              bucket === b.key ? 'ring-1 ring-teal-500/40 border-teal-500' : BUCKET_TONE[b.key] || 'border-slate-200'
            }`}
            data-testid={`bucket-${b.key}`}
          >
            <div className="text-[10px] uppercase tracking-wider opacity-70">{b.label}</div>
            <div className="text-sm font-semibold">₹ {inr(b.amount)}</div>
            <div className="text-[10px] opacity-60">{b.count} invoice{b.count === 1 ? '' : 's'}</div>
          </button>
        ))}
      </div>

      {bucket === 'chase' && (
        <p className="text-xs text-slate-500 -mt-1 mb-3" data-testid="chase-list-note">
          Biggest amounts first, and anyone chased in the last {3} days is left out.
          Tapping WhatsApp opens the chat with the message ready — hit send, and
          Driveline records the chase so the automatic email does not repeat it.
        </p>
      )}

      {/* The list */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 grid place-items-center"><Loader2 className="animate-spin text-slate-300" size={20} /></div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="collections-empty">
            {bucket === 'chase'
              ? 'Nobody to chase today. Everyone overdue has been contacted in the last few days.'
              : 'Nothing outstanding here. Every invoice in this bucket is settled.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left font-semibold px-4 py-2.5">Invoice</th>
                <th className="text-left font-semibold px-4 py-2.5">Customer</th>
                <th className="text-right font-semibold px-4 py-2.5">Outstanding</th>
                <th className="text-right font-semibold px-4 py-2.5">Overdue</th>
                <th className="text-right font-semibold px-4 py-2.5">Chase</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={`${r.kind}-${r.id}`} className="hover:bg-slate-50/60" data-testid={`collections-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <Link
                      to={r.kind === 'gst' ? `/admin/gst/invoices/${r.id}` : `/admin/billing/invoices/${r.id}`}
                      className="font-semibold text-slate-900 hover:text-teal-700 inline-flex items-center gap-1"
                    >
                      {r.number} <ExternalLink size={11} className="opacity-40" />
                    </Link>
                    <div className="text-[11px] text-slate-400">
                      Due {r.due_date || '—'}
                      {r.reminders_sent > 0 && ` · ${r.reminders_sent} reminder${r.reminders_sent === 1 ? '' : 's'} sent`}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-slate-800">{r.customer.name || '—'}</div>
                    <div className="text-[11px] text-slate-400">{r.customer.phone || r.customer.email || 'No contact on file'}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900 tabular-nums">
                    ₹ {inr2(r.balance)}
                    {r.paid > 0 && <div className="text-[10px] font-normal text-slate-400">of ₹ {inr2(r.total)}</div>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`inline-block text-[11px] font-semibold rounded-full border px-2 py-0.5 ${BUCKET_TONE[r.bucket] || 'border-slate-200 text-slate-600'}`}>
                      {r.days_overdue > 0 ? `${r.days_overdue}d` : 'Not due'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => chaseWhatsApp(r)}
                        disabled={!r.customer.phone}
                        title={r.customer.phone ? 'Chase on WhatsApp' : 'No phone number on file'}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 disabled:opacity-30 disabled:hover:bg-transparent"
                        data-testid={`chase-whatsapp-${r.id}`}
                      >
                        <MessageCircle size={15} />
                      </button>
                      <button
                        onClick={() => chaseEmail(r)}
                        disabled={!r.customer.email || busyId === r.id}
                        title={r.customer.email ? 'Email a reminder now' : 'No email address on file'}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-teal-600 hover:bg-teal-50 disabled:opacity-30 disabled:hover:bg-transparent"
                        data-testid={`chase-email-${r.id}`}
                      >
                        {busyId === r.id ? <Loader2 className="animate-spin" size={15} /> : <Mail size={15} />}
                      </button>
                      <button
                        onClick={() => copyLink(r)}
                        disabled={!r.pay_url}
                        title={r.pay_url ? 'Copy the pay link' : 'No gateway connected'}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-teal-600 hover:bg-teal-50 disabled:opacity-30 disabled:hover:bg-transparent"
                        data-testid={`copy-link-${r.id}`}
                      >
                        {copiedId === r.id ? <Check size={15} className="text-emerald-600" /> : <Copy size={15} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CollectionsPage;
